// CSV della "Qualità dell'aria di gruppo": una riga per misura (campione × grandezza), con
// il valore normalizzato usato da mappa e medie (ozono in ppb, AQI sulla scala EPA, cioe' il dato
// del dispositivo per 2), quello grezzo e l'esito dei filtri di `aggregate`. Ci sono tutti i
// campioni, compresi quelli presi in sosta (esito `sosta`), che la mappa non disegna. In coda la
// provenienza della posizione (rilevata, incerta per un buco GPS, stimata) e le coordinate
// stimate, che arrivano gia' nella risposta del backend. Funzioni pure, senza React.

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { airMetricConfig, airMetricOrder } from "../constants/airQuality";
import { METRIC_SOURCE } from "./environmentAggregation";

dayjs.extend(utc);
dayjs.extend(timezone);

export const GROUP_AIR_QUALITY_CSV_COLUMNS = [
    "data_ora",
    "lat",
    "lng",
    "utente",
    "sessione",
    "serie",
    "grandezza",
    "valore",
    "valore_grezzo",
    "unita",
    "esito",
    "posizione",
    "lat_stimata",
    "lng_stimata",
];

/** Esito del campione: dal `rejected` di `aggregate` al codice fisso del CSV. */
const OUTCOME = {
    stationary: "sosta",
    implausible: "fuori_tetto",
    outlier: "fuori_scala",
    uncertainPosition: "posizione_incerta",
};

/** L'AQI è un indice adimensionale (scala EPA 0-500): senza simbolo la cella sembrerebbe un dato mancante. */
const UNIT_OVERRIDE = {
    aqi_epa: "indice EPA",
};

/** Unità in ASCII: il file non ha BOM e Excel storpierebbe µ e ³. */
function csvUnit(metricKey) {
    if (UNIT_OVERRIDE[metricKey]) return UNIT_OVERRIDE[metricKey];
    return (airMetricConfig[metricKey].symbol || "").replace("µ", "u").replace("³", "3");
}

/** Da dove viene la posizione: rilevata dal GPS, stimata lungo il percorso ricostruito, o incerta. */
function positionOf(s) {
    if (!s.positionUncertain) return "rilevata";
    return s.estimatedLat != null && s.estimatedLng != null ? "stimata" : "incerta";
}

/**
 * Righe del CSV dai risultati di `aggregate` per grandezza (`results[metricKey]`), in ordine di
 * timestamp e, a parita', di grandezza. Orari nel fuso `zone` dell'iniziativa. `lat`/`lng` sono
 * quelle della risposta (`originalLat`/`originalLng`), anche per i campioni con posizione
 * stimata, che ha le sue colonne.
 */
export function buildGroupAirQualityCsvRows(results, zone, usernameBySession = {}) {
    const rows = [];
    airMetricOrder.forEach((metricKey, metricIndex) => {
        const result = results ? results[metricKey] : null;
        if (!result) return;
        const unit = csvUnit(metricKey);
        result.samples.forEach(s => {
            const position = positionOf(s);
            rows.push({
                timestamp: s.timestamp,
                metricIndex,
                cells: [
                    dayjs.tz(new Date(s.timestamp), zone).format("YYYY-MM-DD HH:mm:ss"),
                    s.originalLat ?? s.lat,
                    s.originalLng ?? s.lng,
                    usernameBySession[s.sessionId] || "",
                    s.sessionId,
                    METRIC_SOURCE[metricKey].series,
                    metricKey,
                    s.value,
                    s.raw,
                    unit,
                    s.rejected ? OUTCOME[s.rejected] || s.rejected : "usato",
                    position,
                    position === "stimata" ? s.estimatedLat : "",
                    position === "stimata" ? s.estimatedLng : "",
                ],
            });
        });
    });
    rows.sort((a, b) => a.timestamp - b.timestamp || a.metricIndex - b.metricIndex);
    return rows.map(r => r.cells);
}

/** Nome del file: `qualita-aria-gruppo_<oid>_<giorno>_<HHmm>-<HHmm>.csv`. */
export function groupAirQualityCsvFileName(oid, day, from, to) {
    const hhmm = time => (time || "").replace(":", "");
    return `qualita-aria-gruppo_${oid}_${day}_${hhmm(from)}-${hhmm(to)}.csv`;
}
